import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { VehicleAvailabilityWithSupplier } from "@b2bportal/core-cars";
import { FlightShopSelectors } from "@b2bportal/core-flights";
import { HotelsAvailabilitySelectors } from "@b2bportal/core-hotels";
import {
  ChosenProduct,
  PackageType,
  type ChosenCar,
} from "@b2bportal/core-packages";
import type { GetElementClassNameFn } from "@b2bportal/core-themes";
import { Funnel } from "@b2bportal/core-types";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import type { RestrictionCancel } from "@b2bportal/ground-api";
import { DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";
import { CarCheckoutCard } from "@tenant/cars";
import dayjs from "dayjs";
import { formatPackagePickupDropoffDatesFromBenchmark } from "../../hooks/useFormatPackagePickupDropoffDates";
import { BenchmarkFlight } from "./BenchmarkFlight";
import { useIsUpSell } from "@/overrides/packages/hooks";
import {
  PackageBaseSelectors,
  PackageSearchSelectors,
} from "@/store/packages/selectors";

interface TabInfo {
  label: string;
  value: Funnel;
  component: React.ReactNode;
}

const emptyFiatPrice = {
  currencyCode: "CAD",
  currencySymbol: "$",
  value: 0,
};

const emptyPrices = {
  fiat: emptyFiatPrice,
  accountSpecific: {},
};

/**
 * Converts a ChosenCar from benchmark data into a VehicleAvailabilityWithSupplier
 * for use with CarCheckoutCard. Missing fields are filled with default/empty values.
 */
const convertChosenCarToVehicleAvailability = (
  chosenCar: ChosenCar
): VehicleAvailabilityWithSupplier => {
  return {
    vehicle: chosenCar.vehicle,
    hasUnlimitedMileage: chosenCar.uasUnlimitedMileage,
    supplierName: chosenCar.supplierName,
    supplier: {
      code: "",
      name: chosenCar.supplierName,
      logo: "",
    },
    // Required fields with minimal/empty values
    vendorRef: "",
    cancellationPolicy: {} as RestrictionCancel,
    payAtDesk: emptyFiatPrice,
    requiresCreditCard: false,
    totalCharge: emptyPrices,
    opaqueShopRequest: "",
    fees: emptyFiatPrice,
    payNow: emptyPrices,
    pickUp: {
      coordinates: {
        lat: 0,
        lon: 0,
      },
      address: "",
      time: "",
    },
    dropOff: {
      coordinates: {
        lat: 0,
        lon: 0,
      },
      address: "",
      time: "",
    },
    baseRate: emptyFiatPrice,
    supplierRef: chosenCar.supplierName,
  };
};

export const useBenchmark = (cn: GetElementClassNameFn) => {
  const { t, formatDateTime } = useI18nContext();
  // for benchmark, we need to check if anything is "loading"
  const isHotelsLoading = useSelector(HotelsAvailabilitySelectors.isLoading);
  const isFlightsLoading = useSelector(
    FlightShopSelectors.getFlightsLoadingState
  );

  const isLoading = isHotelsLoading || isFlightsLoading;

  const funnelsNeeded = useSelector(
    PackageBaseSelectors.getFunnelsNeededForPackage
  );

  const benchmarkProducts = useSelector(
    PackageBaseSelectors.getBenchmarkProducts
  );

  const packageType = useSelector(
    PackageBaseSelectors.getCurrentActivePackageType
  );

  const isUpSell = useIsUpSell();

  const paxCount = useSelector(
    PackageSearchSelectors.getSearchFormPassengerCount
  );

  const dateRange = useSelector(PackageSearchSelectors.getSearchFormDateRange);

  const nights = useMemo(() => {
    const from = dateRange?.from;
    const until = dateRange?.until;
    if (from == null || until == null) return 0;
    return Math.max(0, dayjs(until).diff(dayjs(from), "days"));
  }, [dateRange?.from, dateRange?.until]);

  const title = useMemo(() => {
    // We have multiple keys to check for singular and plural here, so we can put together the string piece by piece
    // Example string with 2 travelers and 2 nights: "Your package price includes stay (2 nights), round-trip flight (2 passengers) and car, which you can modify in later steps."
    const nightCount = t("core-packages.benchmark.title.nightCount", {
      count: nights,
    });
    const passengerCount = t("core-packages.benchmark.title.passengerCount", {
      count: paxCount,
    });
    switch (packageType) {
      case PackageType.HOTEL_FLIGHT_CAR: {
        return t(
          "core-packages.benchmark.title.priceIncludesStayFlightAndCar",
          { nights: nightCount, passengers: passengerCount }
        );
      }
      case PackageType.HOTEL_FLIGHT: {
        return isUpSell
          ? t("core-packages.benchmark.title.priceIncludesStayFlightChosen", {
              nights: nightCount,
              passengers: passengerCount,
            })
          : t("core-packages.benchmark.title.priceIncludesStayFlight", {
              nights: nightCount,
              passengers: passengerCount,
            });
      }
      case PackageType.HOTEL_CAR: {
        return t("core-packages.benchmark.title.priceIncludesStayCar", {
          nights: nightCount,
        });
      }
      case PackageType.FLIGHT_CAR: {
        return t("core-packages.benchmark.title.priceIncludesFlightCar", {
          passengers: passengerCount,
        });
      }
      default:
        ensureExhaustive(packageType);
    }
  }, [t, nights, paxCount, packageType]);

  // Tabs will only ever be flight or car since hotels comes first
  const tabInfo = useMemo(() => {
    let tabs: TabInfo[] = [];
    if (funnelsNeeded.includes(Funnel.Air)) {
      const benchmarkProduct = benchmarkProducts.find(
        (product) => product.ChosenProduct === ChosenProduct.ChosenFlight
      );
      if (benchmarkProduct) {
        tabs.push({
          label: t("flight"),
          value: Funnel.Air,
          component: (
            <div className={cn("flight-trip-summary")}>
              <BenchmarkFlight
                {...benchmarkProduct.outbound}
                isOutbound={true}
                destination={benchmarkProduct.returning.origin}
              />
              <BenchmarkFlight
                {...benchmarkProduct.returning}
                isOutbound={false}
                destination={benchmarkProduct.outbound.origin}
              />
            </div>
          ),
        });
      }
    }
    if (funnelsNeeded.includes(Funnel.Ground)) {
      const benchmarkProduct = benchmarkProducts.find(
        (product) => product.ChosenProduct === ChosenProduct.ChosenCar
      ) as ChosenCar | undefined;
      if (benchmarkProduct) {
        const carAvailability =
          convertChosenCarToVehicleAvailability(benchmarkProduct);
        // use dates from the benchmark itself.
        const { pickupDate, dropoffDate } =
          formatPackagePickupDropoffDatesFromBenchmark(
            benchmarkProduct.pickupDateTime,
            benchmarkProduct.dropoffDateTime
          );

        const getDateAndTime = (timeStr: string) => {
          const timeDate = new Date(timeStr);

          return formatDateTime(timeDate, DateTimeFormatStyle.MonthDay);
        };

        tabs.push({
          label: t("core-cars.car"),
          value: Funnel.Ground,
          component: (
            <CarCheckoutCard
              car={carAvailability}
              className={cn("car-benchmark")}
            >
              <span className={cn("date-range")}>
                {`${getDateAndTime(pickupDate)} - ${getDateAndTime(dropoffDate)}`}
              </span>
            </CarCheckoutCard>
          ),
        });
      }
    }
    return tabs;
  }, [benchmarkProducts, cn, formatDateTime, funnelsNeeded, t]);

  return {
    context: {
      isLoading,
      // Error if no benchmark products are given from the initial response
      isError: benchmarkProducts.length === 0 || tabInfo.length === 0,
      tabs: tabInfo,
      defaultTab: tabInfo[0]?.value,
      text: {
        title,
      },
    },
  };
};
